import * as React from 'react';
import { useCubeQuery } from '@cubejs-client/react';

import {
    EReviewsMeasureView,
    EReviewsMeasureType,
    EReviewsDimension,
    getReviewsAnalyticsTimeDimensions,
    getReviewsAnalyticsFilters
} from '../reviewsAnalytics';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { OrderScenarioOptions } from 'components/order/model/Order';
import { isString } from 'lib/typeguards';

function buildScenariosData(query: ReturnType<typeof useCubeQuery>) {
    const result = query.resultSet?.series();
    const resultSeries = result && result.length > 0 ? result[0].series : [];
    const data = OrderScenarioOptions.map(scenario => {
        const scenarioData = resultSeries.find(series => series.x === scenario.value);
        return {
            label: isString(scenario.label) ? scenario.label : '',
            value: scenarioData?.value ?? 0
        };
    });
    return {
        data,
        loading: query.isLoading
    };
}

export function useScenariosQueries(
    avgPlatform: string,
    countPlatform: string,
    dateRange: DateRangePickerValue,
    selectedLocationIds: string[]
) {
    const averageScenariosQuery = useCubeQuery({
        measures: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.AVG}`],
        dimensions: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsDimension.SCENARIO}`],
        timeDimensions: getReviewsAnalyticsTimeDimensions(EReviewsMeasureView.REVIEWS_METRICS, dateRange),
        filters: getReviewsAnalyticsFilters(
            EReviewsMeasureView.REVIEWS_METRICS,
            selectedLocationIds,
            avgPlatform === 'all' ? undefined : avgPlatform
        )
    });

    const countScenariosQuery = useCubeQuery({
        measures: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsMeasureType.COUNT}`],
        dimensions: [`${EReviewsMeasureView.REVIEWS_METRICS}.${EReviewsDimension.SCENARIO}`],
        timeDimensions: getReviewsAnalyticsTimeDimensions(EReviewsMeasureView.REVIEWS_METRICS, dateRange),
        filters: getReviewsAnalyticsFilters(
            EReviewsMeasureView.REVIEWS_METRICS,
            selectedLocationIds,
            countPlatform === 'all' ? undefined : countPlatform
        )
    });

    const averageScenariosData = React.useMemo(
        () => buildScenariosData(averageScenariosQuery),
        [averageScenariosQuery]
    );

    const countScenariosData = React.useMemo(
        () => buildScenariosData(countScenariosQuery),
        [countScenariosQuery]
    );

    return {
        averageScenariosData,
        countScenariosData
    };
}
